import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  currentMode: 'light' | 'dark'; // The actual mode being applied
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('system');
  const [currentMode, setCurrentMode] = useState<'light' | 'dark'>('light');
  
  // Function to determine system theme
  const getSystemTheme = (): 'light' | 'dark' => {
    const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDarkMode ? 'dark' : 'light';
  };
  
  // Function to get the actual theme to apply
  const getCurrentTheme = useCallback((): 'light' | 'dark' => {
    if (mode === 'system') {
      return getSystemTheme();
    }
    return mode;
  }, [mode]);
  
  // Load theme preference from localStorage
  const loadThemePreference = (): ThemeMode => {
    try {
      const saved = localStorage.getItem('goatCardsTheme');
      if (saved === 'light' || saved === 'dark' || saved === 'system') {
        return saved;
      }
    } catch (error) {
      console.warn('Could not load theme preference:', error);
    }
    return 'system'; // Default to system
  };
  
  // Save theme preference to localStorage
  const saveThemePreference = (theme: ThemeMode) => {
    try {
      localStorage.setItem('goatCardsTheme', theme);
    } catch (error) {
      console.warn('Could not save theme preference:', error);
    }
  };
  
  // Effect to initialize theme
  useEffect(() => {
    const savedTheme = loadThemePreference();
    setMode(savedTheme);
    setCurrentMode(getCurrentTheme());
    
    // Set up system preference listener (only for system mode)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = () => {
      if (mode === 'system') {
        setCurrentMode(getSystemTheme());
      }
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemChange);
    } else {
      // Older browsers
      mediaQuery.addListener(handleSystemChange);
    }
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemChange);
      } else {
        // Older browsers
        mediaQuery.removeListener(handleSystemChange);
      }
    };
  }, [mode, getCurrentTheme]);
  
  // Update current mode when mode changes
  useEffect(() => {
    setCurrentMode(getCurrentTheme());
  }, [mode, getCurrentTheme]);
  
  // Manual toggle function
  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    setMode(newMode);
    saveThemePreference(newMode);
  };
  
  // Set specific theme function
  const setTheme = (theme: ThemeMode) => {
    setMode(theme);
    saveThemePreference(theme);
  };
  
  return (
    <ThemeContext.Provider value={{ mode, currentMode, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 